/** @jsx jsx */
import React, { useCallback, useRef, useState } from 'react';
import { css, jsx } from '@compiled/react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import OverflowHandler from '@atlassian/jira-filters/src/common/ui/overflow-handler/index.tsx';
import type { OverflowConfigItem } from '@atlassian/jira-filters/src/common/ui/overflow-handler/types.tsx';
import ClearButton from '@atlassian/jira-filters/src/ui/filters/clear-button/index.tsx';
import { useResizeObserver } from '@atlassian/jira-react-use-resize-observer';
import {
	DEFAULT_VISIBLE_USERS,
	ENVIRONMENT,
	EPIC,
	ISSUE_TYPE,
	MIN_FILTER_BAR_WIDTH,
	MIN_VISIBLE_USERS,
	USER_AVATAR_WIDTH,
	VERSION,
	MIN_SEARCH_INPUT_WIDTH,
} from '../../common/constants';
import { NonCriticalErrorBoundary } from '../../common/utils/non-critical-error-boundary';
import {
	useEpicIdsFilter,
	useFixVersionsFilter,
	useHasAppliedFilters,
	useIssueTypeIdsFilter,
} from '../../controllers/top-filter';
import { useIssueTypes } from '../../services/get-issue-types';
import { useProjectId } from '../../services/get-project';
import AssigneesFilter from './assignee-filter';
import ClearFilterLabel from './clear-filter-label';
import EnvironmentFilter from './environment-filter';
import {
	useSelectState,
	useFormatEnvironmentType,
} from './environment-filter/utils/select-state.tsx';
import EpicFilter from './epic-filter';
import IssueTypeFilter from './issue-type-filter';
import TextFilter from './text-filter';
import VersionFilter from './versions-filter';

const TopFilters = () => {
	const [
		hasAppliedFilters,
		{ resetAllFilters, setFixVersionFilter, setEpicIdsFilter, setIssueTypeIdsFilter },
	] = useHasAppliedFilters();
	const projectId = useProjectId();
	const {
		data: issueTypes,
		error: issueTypesFetchError,
		loading: isIssueTypesLoading,
	} = useIssueTypes({ excludeEpic: true });

	const [maxVisibleUsers, setMaxVisibleUsers] = useState<number>(DEFAULT_VISIBLE_USERS);
	const filterBarRef = useRef<HTMLDivElement>(null);
	const [selectedFixVersions] = useFixVersionsFilter();
	const [selectedEpicIds] = useEpicIdsFilter();
	const [selectedIssueTypes] = useIssueTypeIdsFilter();
	const {
		selectedEntryIds: selectedEnvironmentIds,
		onChange: onEnvironmentFilterChange,
		entries: environments,
	} = useSelectState(useFormatEnvironmentType());
	const filters = {
		[ENVIRONMENT]: selectedEnvironmentIds,
		[VERSION]: selectedFixVersions,
		[EPIC]: selectedEpicIds,
		[ISSUE_TYPE]: selectedIssueTypes,
	};

	const handleResize = useCallback(() => {
		// Handle avatar overflow
		if (!filterBarRef.current) return;

		// Handle avatar overflow
		const width = filterBarRef.current.offsetWidth - MIN_SEARCH_INPUT_WIDTH;
		if (width < MIN_FILTER_BAR_WIDTH) {
			const usersToReduce = Math.ceil((MIN_FILTER_BAR_WIDTH - width) / USER_AVATAR_WIDTH);
			const newMax = Math.max(DEFAULT_VISIBLE_USERS - usersToReduce, MIN_VISIBLE_USERS);
			setMaxVisibleUsers(newMax);
		} else {
			setMaxVisibleUsers(DEFAULT_VISIBLE_USERS);
		}
	}, []);

	useResizeObserver({ ref: filterBarRef, onResize: handleResize });

	const itemsBefore: OverflowConfigItem[] = [
		{
			id: ENVIRONMENT,
			isStatic: false,
			renderer: (isNested?: boolean) => (
				<EnvironmentFilter
					key="EnvironmentFilter"
					isNested={isNested}
					onChange={onEnvironmentFilterChange}
					options={environments}
					selectedValues={selectedEnvironmentIds}
				/>
			),
		},
		{
			id: VERSION,
			isStatic: false,
			isHidden: !projectId,
			renderer: (isNested?: boolean) => (
				<>
					{projectId && (
						<NonCriticalErrorBoundary fallback="unmount" eventLocation="fixVersionFilter">
							<VersionFilter
								projectId={projectId}
								isNested={isNested}
								selectedValues={selectedFixVersions}
								onChange={setFixVersionFilter}
							/>
						</NonCriticalErrorBoundary>
					)}
				</>
			),
		},
		{
			id: EPIC,
			isStatic: false,
			renderer: (isNested?: boolean) => (
				<NonCriticalErrorBoundary fallback="unmount" eventLocation="epicFilter">
					<EpicFilter
						key="EpicFilter"
						isNested={isNested}
						selectedValues={selectedEpicIds}
						onChange={setEpicIdsFilter}
					/>
				</NonCriticalErrorBoundary>
			),
		},
		{
			id: ISSUE_TYPE,
			isStatic: false,
			isHidden: isIssueTypesLoading,
			renderer: (isNested?: boolean) => (
				<IssueTypeFilter
					key="IssueTypeFilter"
					isNested={isNested}
					selectedValues={selectedIssueTypes}
					onChange={setIssueTypeIdsFilter}
					issueTypes={issueTypes}
					error={issueTypesFetchError}
				/>
			),
		},
	];

	const itemsAfter: OverflowConfigItem[] = [
		{
			id: 'CLEAR_BUTTON',
			isStatic: true,
			isHidden: !hasAppliedFilters,
			renderer: () => <ClearButton onClick={resetAllFilters} label={<ClearFilterLabel />} />,
		},
	];

	return (
		<Box xcss={topFiltersWrapperStyles} ref={filterBarRef}>
			<TextFilter />
			<AssigneesFilter maxVisibleUsers={maxVisibleUsers} />
			<div css={listFilterContainerStyle}>
				<OverflowHandler
					itemsBefore={itemsBefore}
					itemsAfter={itemsAfter}
					selectedItems={filters}
				/>
			</div>
		</Box>
	);
};

export default TopFilters;

const listFilterContainerStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&:not(:empty)': {
		display: 'flex',
		paddingLeft: token('space.050', '4px'),
		position: 'relative',
		minWidth: 0,
	},
});

const topFiltersWrapperStyles = xcss({
	// enough width to fit expanded search + avatar filter + clear filters button
	minWidth: '405px',
	alignItems: 'center',
	display: 'flex',
	flex: '1 1 0%',
});
